import { KLineData } from "klinecharts";
import { klineLineStyle, lineStyleToKLine } from "./lineStyle";
import { RollingMaxDeque, RollingMinDeque, readStyle } from "./indicatorUtils";

export const createStochastic = (settings: any) => {
  const values = settings?.values ?? {};
  const styles = settings?.styles ?? settings?.styleSettings ?? {};
  const kPeriod = (values["%K Period"] as number) ?? 5;
  const dPeriod = (values["%D Period"] as number) ?? 3;
  const slowing = (values.Slowing as number) ?? 3;
  const kS = readStyle(styles, "k", "#00FF00");
  const dS = readStyle(styles, "d", "#FF0000", "dashed");

  return {
    name: "stochastic",
    shortName: "Stoch",
    calcParams: [kPeriod, dPeriod, slowing],
    series: "normal" as const,
    minValue: 0,
    maxValue: 100,
    figures: [
      { key: "k", title: "%K: ", type: "line" },
      { key: "d", title: "%D: ", type: "line" },
    ],
    styles: {
      lines: [
        klineLineStyle(kS.color, 2, lineStyleToKLine(kS.style)),
        klineLineStyle(dS.color, 2, lineStyleToKLine(dS.style)),
      ],
    },
    regenerateFigures: () => [
      { key: "k", title: "%K: ", type: "line" },
      { key: "d", title: "%D: ", type: "line" },
    ],
    calc: (dataList: KLineData[], indicator: { calcParams?: unknown[] }) => {
      const [kP, dP, slow] = (indicator.calcParams ?? [
        kPeriod,
        dPeriod,
        slowing,
      ]) as [number, number, number];
      const n = dataList.length;
      const results: Array<{ k?: number; d?: number }> = new Array(n);
      const highs: number[] = new Array(n);
      const lows: number[] = new Array(n);
      for (let i = 0; i < n; i++) {
        highs[i] = dataList[i]!.high;
        lows[i] = dataList[i]!.low;
      }

      const maxD = new RollingMaxDeque();
      const minD = new RollingMinDeque();
      // MT-style slowing: sum(close - LL) / sum(HH - LL) over the slowing window
      const numBuf: number[] = new Array(slow);
      const denBuf: number[] = new Array(slow);
      let kBufIdx = 0;
      let kBufCount = 0;
      let numSum = 0;
      let denSum = 0;
      const dBuf: number[] = new Array(dP);
      let dBufIdx = 0;
      let dBufCount = 0;
      let dBufSum = 0;

      for (let i = 0; i < n; i++) {
        maxD.push(i, highs[i]!, highs, kP);
        minD.push(i, lows[i]!, lows, kP);

        if (i < kP - 1) {
          results[i] = {};
          continue;
        }

        const hh = maxD.max(highs);
        const ll = minD.min(lows);
        const num = dataList[i]!.close - ll;
        const den = hh - ll;

        const evictedNum = numBuf[kBufIdx] ?? 0;
        const evictedDen = denBuf[kBufIdx] ?? 0;
        numBuf[kBufIdx] = num;
        denBuf[kBufIdx] = den;
        kBufIdx = (kBufIdx + 1) % slow;
        if (kBufCount < slow) {
          kBufCount++;
          numSum += num;
          denSum += den;
        } else {
          numSum += num - evictedNum;
          denSum += den - evictedDen;
        }

        if (kBufCount < slow) {
          results[i] = {};
          continue;
        }

        const smoothedK = denSum === 0 ? 50 : (100 * numSum) / denSum;
        const dEvicted = dBuf[dBufIdx] ?? 0;
        dBuf[dBufIdx] = smoothedK;
        dBufIdx = (dBufIdx + 1) % dP;
        if (dBufCount < dP) {
          dBufCount++;
          dBufSum += smoothedK;
        } else {
          dBufSum += smoothedK - dEvicted;
        }

        if (dBufCount < dP) {
          results[i] = { k: smoothedK };
        } else {
          results[i] = { k: smoothedK, d: dBufSum / dP };
        }
      }

      return results;
    },
  };
};
